// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconButton, Select, StatusBanner, TextInput } from '@loa/ui-components';
import type { AnnotationBinding, BindingCatalogue } from './types';
import { NB_FIELD_TYPES } from './types';
import { canvasToPdf, pdfToCanvas } from './pdfCoords';

const RENDER_SCALE = 1.25;

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
    <path
      d={dir === 'left' ? 'M8 1L2 7L8 13' : 'M2 1L8 7L2 13'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface Props {
  pdfData: ArrayBuffer | null;
  bindings: AnnotationBinding[];
  catalogue: BindingCatalogue | null;
  /**
   * The template's parameter schema — its fields become bindable `param.<id>`
   * data sources ("This form's parameters"), and a radio/enum field's options
   * drive the CHECK "tick when" picker.
   */
  paramSchema?: Record<string, unknown> | null;
  onChange: (bindings: AnnotationBinding[]) => void;
}

interface ParamField {
  id: string;
  label: string;
  type: string;
  options: string[];
}

/** The authored parameter fields (from the schema builder) as bindable sources. */
function paramFields(schema?: Record<string, unknown> | null): ParamField[] {
  const questions = Array.isArray((schema as { questions?: unknown })?.questions)
    ? ((schema as { questions: unknown[] }).questions)
    : [];
  const out: ParamField[] = [];
  for (const q of questions) {
    if (!q || typeof q !== 'object') continue;
    const field = q as { id?: unknown; label?: unknown; type?: unknown; options?: unknown };
    if (typeof field.id !== 'string' || !field.id) continue;
    const rawOptions = Array.isArray(field.options) ? field.options : [];
    out.push({
      id: field.id,
      label: typeof field.label === 'string' && field.label ? field.label : field.id,
      type: typeof field.type === 'string' ? field.type : 'string',
      options: rawOptions
        .map((o) => (typeof o === 'string' ? o : (o as { value?: unknown })?.value))
        .filter((v): v is string => typeof v === 'string' && v.length > 0),
    });
  }
  return out;
}

/**
 * Click-to-place coordinate-overlay editor: renders each PDF page to a canvas
 * (pdfjs), lets the author click to drop a binding, pick its data key + field
 * type, drag to reposition, or fine-tune page/x/y. Coordinates are captured in
 * PDF user space (see {@link canvasToPdf}). SIGNATURE bindings carry no source
 * and mark where the applicant signs (≥1 required to publish). Styled with the
 * Finerty design tokens + shared UI components so it renders identically in the
 * Admin and Advisor UIs.
 */
export default function PdfAnnotationEditor({ pdfData, bindings, catalogue, paramSchema, onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageHeightPdf, setPageHeightPdf] = useState(0);
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const pdfDocRef = useRef<unknown>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ id: string; pointerId: number } | null>(null);

  // Load the PDF document when the bytes change.
  useEffect(() => {
    let cancelled = false;
    if (!pdfData) {
      pdfDocRef.current = null;
      setNumPages(0);
      return;
    }
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const workerUrl = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = (workerUrl as { default: string }).default;
        const doc = await pdfjs.getDocument({ data: pdfData.slice(0) }).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setPageNum(1);
        setRenderError(null);
      } catch (e) {
        if (!cancelled) setRenderError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfData]);

  // Render the current page to the canvas.
  useEffect(() => {
    let cancelled = false;
    const doc = pdfDocRef.current as { getPage: (n: number) => Promise<unknown> } | null;
    if (!doc || numPages === 0) return;
    (async () => {
      try {
        const page = (await doc.getPage(pageNum)) as {
          getViewport: (o: { scale: number }) => { width: number; height: number };
          render: (o: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
        };
        const viewport = page.getViewport({ scale: RENDER_SCALE });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        setCanvasSize({ w: viewport.width, h: viewport.height });
        setPageHeightPdf(viewport.height / RENDER_SCALE);
      } catch (e) {
        if (!cancelled) setRenderError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageNum, numPages]);

  const defaultSource = catalogue?.groups?.[0]?.entries?.[0]?.key ?? '';

  const addBindingAt = useCallback(
    (canvasX: number, canvasY: number) => {
      const { x, y } = canvasToPdf(canvasX, canvasY, RENDER_SCALE, pageHeightPdf);
      const id = crypto.randomUUID();
      const binding: AnnotationBinding = {
        id,
        page: pageNum,
        x,
        y,
        fontSize: 10,
        fieldType: 'TEXT',
        source: defaultSource,
      };
      onChange([...bindings, binding]);
      setSelectedId(id);
    },
    [bindings, defaultSource, onChange, pageNum, pageHeightPdf],
  );

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).dataset.marker) return; // clicking a marker selects it, handled separately
    const rect = e.currentTarget.getBoundingClientRect();
    addBindingAt(e.clientX - rect.left, e.clientY - rect.top);
  };

  const updateBinding = (id: string, patch: Partial<AnnotationBinding>) => {
    onChange(bindings.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const removeBinding = (id: string) => {
    onChange(bindings.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // Drag-to-move: pressing a marker starts a drag (and selects it); moving the
  // pointer repositions the binding in PDF user space; releasing ends it. Pointer
  // capture keeps the move/up events flowing to the marker even if the cursor
  // outruns it.
  const beginDrag = (e: React.PointerEvent<HTMLDivElement>, b: AnnotationBinding) => {
    e.stopPropagation();
    setSelectedId(b.id);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* not supported (e.g. jsdom) — drag still works via bubbling */ }
    dragRef.current = { id: b.id, pointerId: e.pointerId };
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || !overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const { x, y } = canvasToPdf(e.clientX - rect.left, e.clientY - rect.top, RENDER_SCALE, pageHeightPdf);
    updateBinding(drag.id, { x, y });
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch { /* not supported */ }
    dragRef.current = null;
  };

  // Catalogue system fields (excluding the generic `param.<fieldId>` convention
  // row) + this template's own authored parameters as real `param.<id>` sources.
  const params = paramFields(paramSchema);
  const sourceOptions = [
    ...(catalogue?.groups ?? []).flatMap((g) =>
      g.entries
        .filter((entry) => !entry.key.startsWith('param.'))
        .map((entry) => ({ value: entry.key, text: `${g.name} · ${entry.label}` })),
    ),
    ...params.map((f) => ({ value: `param.${f.id}`, text: `This form · ${f.label}` })),
  ];

  const selected = bindings.find((b) => b.id === selectedId) ?? null;
  // The param field the selected CHECK binds to, if any (drives the "tick when" picker).
  const selectedParam = selected?.source?.startsWith('param.')
    ? params.find((f) => `param.${f.id}` === selected.source)
    : undefined;
  const pageBindings = bindings.filter((b) => b.page === pageNum);
  const signatureCount = bindings.filter((b) => b.fieldType === 'SIGNATURE').length;

  if (!pdfData) {
    return (
      <div data-testid="nb-annotation-no-pdf">
        <StatusBanner
          variant="neutral"
          position="inline"
          visible
          dismissible={false}
          message="Upload the blank provider form PDF to start placing bindings."
        />
      </div>
    );
  }

  const markerStyle = (b: AnnotationBinding): React.CSSProperties => {
    const isSig = b.fieldType === 'SIGNATURE';
    const isSelected = selectedId === b.id;
    return {
      position: 'absolute',
      transform: 'translateX(-2px)',
      background: isSig
        ? 'var(--color-warning-bg)'
        : isSelected
          ? 'var(--color-purple-primary)'
          : 'var(--color-lilac-06)',
      color: isSig
        ? 'var(--color-warning-text)'
        : isSelected
          ? 'var(--color-white)'
          : 'var(--color-purple-primary)',
      border: `1px solid ${isSelected ? 'var(--color-purple-primary)' : 'var(--color-lilac-04)'}`,
      boxShadow: isSelected ? '0 0 0 2px var(--color-lilac-04)' : 'none',
      borderRadius: 'var(--radius-button)',
      padding: '2px 6px',
      fontSize: 'var(--font-size-body-04)',
      whiteSpace: 'nowrap',
      cursor: 'move',
      touchAction: 'none',
      userSelect: 'none',
    };
  };

  return (
    <div data-testid="nb-annotation-editor">
      {renderError && (
        <StatusBanner
          variant="error"
          position="inline"
          visible
          dismissible={false}
          message={renderError}
        />
      )}
      <div style={{ marginBottom: 'var(--spacing-sm)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
        <IconButton
          variant="square-outlined"
          size="sm"
          aria-label="Previous page"
          data-testid="nb-page-prev"
          disabled={pageNum <= 1}
          onClick={() => setPageNum((p) => Math.max(1, p - 1))}
          icon={<Chevron dir="left" />}
        />
        <span data-testid="nb-page-indicator" style={{ fontSize: 'var(--font-size-body-03)' }}>
          {pageNum} / {numPages}
        </span>
        <IconButton
          variant="square-outlined"
          size="sm"
          aria-label="Next page"
          data-testid="nb-page-next"
          disabled={pageNum >= numPages}
          onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
          icon={<Chevron dir="right" />}
        />
        <span style={{ color: 'var(--color-secondary-text)', fontSize: 'var(--font-size-body-03)' }}>
          Click to place a binding · drag a marker to move it.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          ref={overlayRef}
          role="presentation"
          onClick={onOverlayClick}
          style={{
            position: 'relative',
            width: canvasSize.w,
            height: canvasSize.h,
            border: '1px solid var(--color-grey-04)',
            cursor: 'crosshair',
            flexShrink: 0,
          }}
        >
          <canvas ref={canvasRef} style={{ display: 'block' }} />
          {pageBindings.map((b) => {
            const { canvasX, canvasY } = pdfToCanvas(b.x, b.y, RENDER_SCALE, pageHeightPdf);
            const isSig = b.fieldType === 'SIGNATURE';
            return (
              <div
                key={b.id}
                data-marker="1"
                data-testid={`nb-marker-${b.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedId(b.id);
                }}
                onPointerDown={(e) => beginDrag(e, b)}
                onPointerMove={onDragMove}
                onPointerUp={endDrag}
                title={isSig ? 'Signature region — drag to move' : `${b.source ?? ''} — drag to move`}
                style={{ ...markerStyle(b), left: canvasX, top: canvasY - 16 }}
              >
                {isSig ? '✍ SIGN' : (b.source ?? '?').replace(/^.*\./, '')}
              </div>
            );
          })}
        </div>

        <div style={{ minWidth: 260, flex: 1 }} data-testid="nb-binding-inspector">
          {selected ? (
            <div>
              <h4 style={{ marginTop: 0, fontSize: 'var(--font-size-body-01)' }}>Binding</h4>
              <Select
                fluid
                label="Field type"
                value={selected.fieldType}
                options={NB_FIELD_TYPES.map((t) => ({ value: t, text: t }))}
                onChange={(e) => updateBinding(selected.id, { fieldType: e.target.value })}
                data-testid="nb-binding-fieldtype"
              />
              {selected.fieldType !== 'SIGNATURE' && (
                <div style={{ marginTop: 'var(--spacing-sm)' }}>
                  <Select
                    fluid
                    label="Data source"
                    placeholder="Select a data source…"
                    value={selected.source ?? ''}
                    options={sourceOptions}
                    onChange={(e) => updateBinding(selected.id, { source: e.target.value })}
                    data-testid="nb-binding-source"
                  />
                </div>
              )}
              {selected.fieldType === 'CHECK' && (
                <div style={{ marginTop: 'var(--spacing-sm)' }}>
                  {selectedParam && selectedParam.options.length > 0 ? (
                    <Select
                      fluid
                      label="Tick when the answer is"
                      value={selected.checkWhen ?? ''}
                      options={[
                        { value: '', text: 'Any value (tick if answered)' },
                        ...selectedParam.options.map((o) => ({ value: o, text: o })),
                      ]}
                      onChange={(e) => updateBinding(selected.id, { checkWhen: e.target.value || null })}
                      data-testid="nb-binding-checkwhen"
                    />
                  ) : (
                    <TextInput
                      fluid
                      label="Tick when the answer equals"
                      placeholder="e.g. true — leave blank to tick if answered"
                      value={selected.checkWhen ?? ''}
                      onChange={(e) => updateBinding(selected.id, { checkWhen: e.target.value || null })}
                      data-testid="nb-binding-checkwhen"
                    />
                  )}
                </div>
              )}
              <div style={{ marginTop: 'var(--spacing-sm)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <TextInput
                  fluid
                  type="number"
                  label="Page"
                  min={1}
                  max={numPages || 1}
                  value={String(selected.page)}
                  onChange={(e) =>
                    updateBinding(selected.id, {
                      page: Math.min(Math.max(1, Math.round(Number(e.target.value)) || 1), numPages || 1),
                    })
                  }
                  data-testid="nb-binding-page"
                />
                <TextInput
                  fluid
                  type="number"
                  label="X"
                  step="0.1"
                  value={String(selected.x)}
                  onChange={(e) => updateBinding(selected.id, { x: Number(e.target.value) })}
                  data-testid="nb-binding-x"
                />
                <TextInput
                  fluid
                  type="number"
                  label="Y"
                  step="0.1"
                  value={String(selected.y)}
                  onChange={(e) => updateBinding(selected.id, { y: Number(e.target.value) })}
                  data-testid="nb-binding-y"
                />
              </div>
              <div style={{ marginTop: 'var(--spacing-sm)' }}>
                <Button
                  variant="outlined"
                  colour="red"
                  size="sm"
                  onClick={() => removeBinding(selected.id)}
                  data-testid="nb-binding-remove"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <StatusBanner
              variant="neutral"
              position="inline"
              visible
              dismissible={false}
              message="Select a binding to edit it, or click the page to add one."
            />
          )}
          <div style={{ marginTop: 'var(--spacing-lg)', fontSize: 'var(--font-size-body-03)' }}>
            <strong>{signatureCount}</strong> signature region(s) · <strong>{bindings.length}</strong> binding(s)
          </div>
        </div>
      </div>
    </div>
  );
}
